/**
 * The tuning model: what the dial position *means* in DSP terms.
 *
 * Pure functions, no Web Audio — so the curves can be reasoned about (and tested) on their own,
 * and the receiver just applies them.
 *
 * Two ideas carry the whole feel:
 *
 * 1. **The band is not uniform.** Every frequency has its own static texture, derived from
 *    deterministic value noise over the dial position. Sweeping the dial therefore moves through
 *    audibly different regions of hiss and rumble instead of one flat hum — and a given
 *    frequency always sounds like itself, which is what makes a band memorable enough to
 *    navigate by ear.
 *
 * 2. **A signal arrives through the filter, not through the volume knob.** As you approach a
 *    transmission its passband opens (narrow and telephone-thin far out, wide and intelligible
 *    on frequency), its level rises on a squared curve, the noise floor ducks under it, and a
 *    heterodyne whistle beats against the carrier — loudest during the approach, gone once
 *    you're on frequency. That whistle is the thing that makes tuning feel like tuning.
 */

export interface BandTexture {
  /** Noise-colour mix for this part of the band. */
  white: number;
  pink: number;
  brown: number;
  /** Static bandpass edges, in Hz. */
  lowCutHz: number;
  highCutHz: number;
}

export interface SignalShape {
  /** 0 → nothing there, 1 → dead on frequency. */
  strength: number;
  /** Passband of the transmission itself. */
  bandpassHz: number;
  bandpassQ: number;
  lowpassHz: number;
  /** Transmission level. */
  gain: number;
  /** Beat note against the carrier: Hz and level. */
  heterodyneHz: number;
  heterodyneGain: number;
  /** How much the noise floor gets out of the way. */
  noiseDuck: number;
}

/** Deterministic hash → [0, 1). Same frequency, same static, every session. */
function hash(n: number): number {
  const x = Math.sin(n * 127.1) * 43758.5453;
  return x - Math.floor(x);
}

/** Smooth value noise over a 1-D coordinate. */
function valueNoise(x: number): number {
  const i = Math.floor(x);
  const f = x - i;
  const u = f * f * (3 - 2 * f); // smoothstep
  return hash(i) * (1 - u) + hash(i + 1) * u;
}

/** Two octaves of value noise — broad regions with finer variation inside them. */
function fbm(x: number): number {
  return valueNoise(x) * 0.65 + valueNoise(x * 3.7 + 11.3) * 0.35;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/**
 * The static at a given dial position. Regions are ~90 kHz wide, so a slow sweep crosses several
 * distinct textures and a fine-tune stays inside one.
 */
export function bandTexture(khz: number): BandTexture {
  const broad = fbm(khz / 90);
  const fine = fbm(khz / 23 + 57.2);

  return {
    // Bright hissy regions vs. dull rumbling ones.
    white: lerp(0.12, 0.6, broad),
    pink: lerp(0.7, 1.15, fine),
    brown: lerp(0.15, 0.85, 1 - broad),
    lowCutHz: lerp(70, 340, fine),
    highCutHz: lerp(1900, 7200, broad * 0.7 + fine * 0.3),
  };
}

/**
 * Signal strength as a function of how far off frequency you are.
 *
 * A Gaussian rather than a linear ramp: the transmission is inaudible until you are genuinely
 * near it, then comes up fast — which is what makes finding one feel like finding something.
 */
export function signalStrength(dialKhz: number, signalKhz: number, widthKhz: number): number {
  const offset = (dialKhz - signalKhz) / widthKhz;
  return Math.exp(-(offset * offset) * 1.6);
}

export interface SignalShapeOptions {
  /** Hz of beat note per kHz off frequency. */
  heterodynePerKhz?: number;
}

export function signalShape(
  dialKhz: number,
  signalKhz: number,
  widthKhz: number,
  options: SignalShapeOptions = {},
): SignalShape {
  const strength = signalStrength(dialKhz, signalKhz, widthKhz);
  const detuneKhz = Math.abs(dialKhz - signalKhz);
  const heterodynePerKhz = options.heterodynePerKhz ?? 19;

  // The passband opens as you close in: a narrow, resonant slot far out (unintelligible but
  // clearly *something*), a wide open channel on frequency.
  const bandpassHz = lerp(760, 1500, strength);
  const bandpassQ = lerp(7.5, 0.75, strength);
  const lowpassHz = lerp(1100, 6800, strength * strength);

  // Squared: quiet fringes, decisive centre.
  const gain = strength * strength;

  // The whistle peaks mid-approach and disappears on frequency — 4·s·(1−s) peaks at s = 0.5.
  const heterodyneGain = 4 * strength * (1 - strength) * 0.09;
  const heterodyneHz = clamp(detuneKhz * heterodynePerKhz, 90, 3200);

  return {
    strength,
    bandpassHz,
    bandpassQ,
    lowpassHz,
    gain,
    heterodyneHz,
    heterodyneGain,
    // How far the noise floor drops under a lock: to 18% dead on frequency.
    //
    // Chapter 7 deepened this from 28%. Measured through the mixer, a locked signal was reading
    // only 1.9x the static left underneath it, which is not "the transmission dominates" — it is
    // a voice competing with hiss. Raising the transmissions instead would have pushed peaks into
    // the safety limiter, and this is the more honest lever anyway: a strong carrier captures a
    // receiver's AGC and the hiss really does fall away.
    noiseDuck: 1 - 0.82 * strength,
  };
}

/**
 * A brief lift in the static while the dial is actually moving — the sound of a wiper dragging
 * across a coil. Velocity is kHz/second.
 */
export function dialScrub(velocityKhzPerSecond: number): number {
  return clamp(Math.abs(velocityKhzPerSecond) / 900, 0, 1) * 0.5;
}
